<template>
  <section>
    <b-table
      :items="activityItems"
      :fields="fields"
      table-class="table-minimal"
      :empty-text="$t('common.noRecordsToShow')"
      show-empty
    >
      <template #cell(activity)="data">
        {{ getActivityLabel(data.item) }}
      </template>
      <template #cell(username)="data">
        {{ data.item.username || 'Stream' }}
      </template>
    </b-table>
  </section>
</template>

<script setup>
import moment from 'moment';
import { computed } from 'vue';
import i18n from '@/i18n';
import { toFormattedDate } from '@/utils/date';

const ACTIVITY_KEYS = {
  FURLOUGHED: 'FURLOUGHED',
  GRANT_ASSIGNED: 'GRANT_ASSIGNED',
  GRANT_CLAIMED: 'GRANT_CLAIMED',
  OPTOUT_INITIATED: 'OPTOUT_INITIATED',
  OPTOUT_COMPLETED: 'OPTOUT_COMPLETED',
  PAUSED: 'paused',
  TRANSFERS: 'TRANSFERS',
  UNFURLOUGHED: 'UNFURLOUGHED',
  PROPSCHANGE: 'PROPSCHANGE',
  SALARY_CHANGE: 'SALARY_CHANGE',
};

const ACTIVITY_LABEL_KEYS = {
  [ACTIVITY_KEYS.FURLOUGHED]: 'employee.activityLog.labels.furloughed',
  [ACTIVITY_KEYS.GRANT_ASSIGNED]: 'employee.activityLog.labels.grantAssigned',
  [ACTIVITY_KEYS.GRANT_CLAIMED]: 'employee.activityLog.labels.grantClaimed',
  [ACTIVITY_KEYS.OPTOUT_INITIATED]: 'employee.activityLog.labels.optOutInitiated',
  [ACTIVITY_KEYS.OPTOUT_COMPLETED]: 'employee.activityLog.labels.optOutCompleted',
  [ACTIVITY_KEYS.PAUSED]: item =>
    item.properties.new.paused ? 'employee.activityLog.labels.paused' : 'employee.activityLog.labels.unpaused',
  [ACTIVITY_KEYS.TRANSFERS]: item =>
    item.properties.new.transfers_disabled
      ? 'employee.activityLog.labels.payTransfersDisabled'
      : 'employee.activityLog.labels.payTransfersEnabled',
  [ACTIVITY_KEYS.UNFURLOUGHED]: 'employee.activityLog.labels.unfurloughed',
  [ACTIVITY_KEYS.PROPSCHANGE]: 'employee.activityLog.labels.employeeDetailsUpdated',
  [ACTIVITY_KEYS.SALARY_CHANGE]: 'employee.activityLog.labels.salaryChange',
};

const ACTIVITY_TYPES = [
  ACTIVITY_KEYS.FURLOUGHED,
  ACTIVITY_KEYS.GRANT_ASSIGNED,
  ACTIVITY_KEYS.GRANT_CLAIMED,
  ACTIVITY_KEYS.TRANSFERS,
  ACTIVITY_KEYS.UNFURLOUGHED,
  ACTIVITY_KEYS.PROPSCHANGE,
  ACTIVITY_KEYS.SALARY_CHANGE,
];

const PROPERTY_BASED_ACTIVITIES = [ACTIVITY_KEYS.PAUSED];

const fields = computed(() => [
  {
    key: 'logged_at',
    label: i18n.t('employee.activityLog.columns.dateTime'),
    sortable: true,
    formatter: value => toFormattedDate(value, 'YYYY-MM-DD HH:mm'),
  },
  {
    key: 'username',
    label: i18n.t('employee.activityLog.columns.performedBy'),
    sortable: true,
  },
  {
    key: 'activity',
    label: i18n.t('employee.activityLog.columns.action'),
  },
]);

const props = defineProps({
  activity: {
    type: Array,
    default: () => [],
  },
  optout: {
    type: Object,
    default: null,
  },
  salaryHistory: {
    type: Array,
    default: () => [],
  },
});

const activityItems = computed(() => {
  const activitySource = Array.isArray(props.activity) ? props.activity : [];

  const items = activitySource.filter(item => {
    return (
      ACTIVITY_TYPES.includes(item.activity) ||
      (item.properties?.new &&
        PROPERTY_BASED_ACTIVITIES.some(
          property => typeof item.properties.new === 'object' && property in item.properties.new,
        ) &&
        !(item.activity === ACTIVITY_KEYS.PROPSCHANGE && !item.username))
    );
  });

  items.push(
    ...props.salaryHistory
      .filter(item => item.new.salary && item.old.salary && item.new.salary != item.old.salary && item.username)
      .map(item => ({
        activity: ACTIVITY_KEYS.SALARY_CHANGE,
        logged_at: item.created_at,
        username: item.username,
        properties: { new: item.new, old: item.old },
      })),
  );

  // Add optout entries from the new endpoint
  if (props.optout?.initiated_at) {
    // Always show "Opt-out Initiated" entry
    items.push({
      activity: ACTIVITY_KEYS.OPTOUT_INITIATED,
      logged_at: props.optout.initiated_at,
      username: props.optout.username,
      properties: {},
    });

    // Show "Opt-out Completed" entry if completed
    if (props.optout.completed_at) {
      items.push({
        activity: ACTIVITY_KEYS.OPTOUT_COMPLETED,
        logged_at: props.optout.completed_at,
        username: props.optout.username,
        properties: {},
      });
    }
  }

  return items.sort((a, b) => moment(b.logged_at).diff(a.logged_at));
});

const getActivityLabel = item => {
  const key = getActivityKey(item);
  const labelKey =
    typeof ACTIVITY_LABEL_KEYS[key] === 'function' ? ACTIVITY_LABEL_KEYS[key](item) : ACTIVITY_LABEL_KEYS[key];
  return labelKey ? i18n.t(labelKey) : '';
};

const getActivityKey = item =>
  item.properties?.new
    ? Object.keys(item.properties.new).find(key => PROPERTY_BASED_ACTIVITIES.includes(key)) || item.activity
    : item.activity;
</script>
